/**
 * POPrintModal — printable Purchase Order view (A4, @media print scoped).
 *
 * 2026-02-19 — spun up alongside the Round Off feature so the physical
 * copy handed to the vendor matches the on-screen total to the paisa.
 * Shows the Subtotal / GST / Round Off / Final Total breakdown exactly
 * like the Detail Drawer tfoot.
 *
 * Scope rule: `document.body.classList.add('printing-po')` while open so
 * the global stylesheet's `@media print` rules can hide every other DOM
 * node without us having to teach them about this component.
 */
import React, { useEffect } from 'react';
import { Printer, X } from 'lucide-react';
import useEscapeKey from '../../hooks/useEscapeKey';

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function POPrintModal({ po, products, clinicName = 'Audinexa Clinic', branchName, vendor, onClose }) {
  useEscapeKey(onClose);

  useEffect(() => {
    document.body.classList.add('printing-po');
    return () => document.body.classList.remove('printing-po');
  }, []);

  const subtotal = Number(po.subtotal || 0);
  const gst = Number(po.gst_amount || 0);
  const roundOff = Number(po.round_off || 0);
  const finalTotal = Number(po.total || 0);

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 flex items-start justify-center p-4 overflow-auto po-print-root" data-testid="ha-po-print-modal">
      <div className="bg-white shadow-2xl w-full max-w-[800px] po-print-sheet">
        <header className="flex items-center justify-between px-4 py-2 border-b border-slate-200 no-print">
          <div className="text-sm font-bold text-slate-700">Purchase Order · Print Preview</div>
          <div className="flex items-center gap-2">
            <button onClick={() => window.print()} data-testid="ha-po-print-btn"
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-indigo-600 hover:bg-indigo-700 text-white rounded shadow-sm">
              <Printer size={12} /> Print
            </button>
            <button onClick={onClose} data-testid="ha-po-print-close"
              className="w-7 h-7 rounded hover:bg-slate-100 flex items-center justify-center text-slate-500"><X size={14} /></button>
          </div>
        </header>

        {/* A4 sheet */}
        <div className="po-sheet p-8 text-slate-800">
          <div className="flex items-start justify-between mb-5 pb-3 border-b-2 border-slate-800">
            <div>
              <div className="text-xl font-bold tracking-tight">{clinicName}</div>
              {branchName && <div className="text-xs text-slate-600">{branchName}</div>}
            </div>
            <div className="text-right">
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Purchase Order</div>
              <div className="text-lg font-mono font-bold">{po.po_no}</div>
              <div className="text-[10px] text-slate-500">{po.created_at ? new Date(po.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}</div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-5 text-xs">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Vendor</div>
              <div className="font-semibold text-sm">{po.vendor_name || vendor?.name || po.vendor_id}</div>
              {vendor?.gstin && <div className="text-[11px] text-slate-600">GSTIN: <span className="font-mono">{vendor.gstin}</span></div>}
              {vendor?.address && <div className="text-[11px] text-slate-600 whitespace-pre-line">{vendor.address}</div>}
              {vendor?.phone && <div className="text-[11px] text-slate-600">Phone: {vendor.phone}</div>}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Order Details</div>
              <div className="text-[11px]"><span className="text-slate-500">Expected:</span> <span className="font-semibold">{po.expected_date || '—'}</span></div>
              <div className="text-[11px]"><span className="text-slate-500">Status:</span> <span className="font-semibold uppercase">{(po.status || '').replace('_', ' ')}</span></div>
              {po.notes && <div className="text-[11px] mt-1"><span className="text-slate-500">Notes:</span> {po.notes}</div>}
            </div>
          </div>

          <table className="w-full text-xs border border-slate-300 mb-4" style={{ borderCollapse: 'collapse' }}>
            <thead>
              <tr className="bg-slate-100 text-[10px] uppercase tracking-wider">
                <th className="px-2 py-1.5 text-left border border-slate-300">#</th>
                <th className="px-2 py-1.5 text-left border border-slate-300">Product</th>
                <th className="px-2 py-1.5 text-right border border-slate-300">Qty</th>
                <th className="px-2 py-1.5 text-right border border-slate-300">Unit (₹)</th>
                <th className="px-2 py-1.5 text-right border border-slate-300">GST %</th>
                <th className="px-2 py-1.5 text-right border border-slate-300">Line Total (incl GST)</th>
              </tr>
            </thead>
            <tbody>
              {po.lines.map((ln, i) => {
                const p = (products || {})[ln.product_id];
                const lineTotal = ln.qty * ln.unit_cost * (1 + (ln.gst_rate || 0) / 100);
                return (
                  <tr key={i}>
                    <td className="px-2 py-1 border border-slate-300 tabular-nums">{i + 1}</td>
                    <td className="px-2 py-1 border border-slate-300">
                      <div className="font-semibold">{p ? `${p.brand} ${p.model}` : ln.product_id}</div>
                      {ln.spec && Object.keys(ln.spec).length > 0 && (
                        <div className="text-[10px] text-slate-500">
                          {Object.entries(ln.spec).filter(([_, v]) => v).map(([k, v]) => `${k}: ${v}`).join(' · ')}
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-1 text-right border border-slate-300 tabular-nums">{ln.qty}</td>
                    <td className="px-2 py-1 text-right border border-slate-300 tabular-nums">{fmtINR(ln.unit_cost)}</td>
                    <td className="px-2 py-1 text-right border border-slate-300 tabular-nums">{ln.gst_rate}%</td>
                    <td className="px-2 py-1 text-right border border-slate-300 tabular-nums">{fmtINR(lineTotal)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Totals block — mirrors the Detail Drawer tfoot. Round Off row
              only renders when non-zero so the common case stays clean. */}
          <div className="flex justify-end mb-6">
            <table className="text-xs" style={{ minWidth: '320px' }}>
              <tbody>
                <tr>
                  <td className="py-1 text-right pr-4 text-slate-600">Subtotal</td>
                  <td className="py-1 text-right tabular-nums font-semibold" data-testid="ha-po-print-subtotal">{fmtINR(subtotal)}</td>
                </tr>
                <tr>
                  <td className="py-1 text-right pr-4 text-slate-600">GST</td>
                  <td className="py-1 text-right tabular-nums font-semibold" data-testid="ha-po-print-gst">{fmtINR(gst)}</td>
                </tr>
                <tr className="border-t border-slate-300">
                  <td className="py-1 text-right pr-4 text-slate-600">Total (incl GST)</td>
                  <td className="py-1 text-right tabular-nums font-semibold" data-testid="ha-po-print-total-pre">{fmtINR(subtotal + gst)}</td>
                </tr>
                {roundOff !== 0 && (
                  <tr data-testid="ha-po-print-round-off-row">
                    <td className="py-1 text-right pr-4 text-slate-600">Round Off</td>
                    <td className="py-1 text-right tabular-nums font-semibold">
                      {roundOff >= 0 ? '+' : ''}{fmtINR(roundOff)}
                    </td>
                  </tr>
                )}
                <tr className="border-t-2 border-slate-800">
                  <td className="py-2 text-right pr-4 font-bold uppercase text-[11px] tracking-wider">Final Total</td>
                  <td className="py-2 text-right tabular-nums font-bold text-base" data-testid="ha-po-print-final-total">{fmtINR(finalTotal)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid grid-cols-2 gap-16 mt-16 pt-6 text-[10px] text-slate-600">
            <div className="border-t border-slate-400 pt-2 text-center">Prepared By</div>
            <div className="border-t border-slate-400 pt-2 text-center">Authorised Signatory</div>
          </div>

          <div className="mt-6 pt-2 border-t border-slate-200 text-[9px] text-slate-400 text-center">
            This is a computer-generated purchase order. Round Off shown as a separate line reconciles the saved total to the vendor's printed invoice.
          </div>
        </div>
      </div>

      {/* Scoped print CSS. We mount this inline so it only activates while
          the modal is open — avoids the global stylesheet having to know
          about this component. */}
      <style>{`
        @media print {
          body.printing-po > *:not(.po-print-root) { display: none !important; }
          .po-print-root { position: static !important; background: white !important; padding: 0 !important; overflow: visible !important; }
          .po-print-root .no-print { display: none !important; }
          .po-print-sheet { box-shadow: none !important; max-width: 100% !important; }
          .po-sheet { padding: 10mm !important; }
          @page { size: A4; margin: 10mm; }
        }
      `}</style>
    </div>
  );
}
